<template>
  <div class="media-manager">
    <div class="page-header">
      <h2><span class="breadcrumb-level">内容运营</span><span class="breadcrumb-sep">/</span><span class="breadcrumb-level">内容</span><span class="breadcrumb-sep">/</span><span class="breadcrumb-level breadcrumb-current">素材管理</span></h2>
      <div class="header-actions">
        <el-button size="small" type="primary" @click="showUploadDialog" style="height:28px;font-size:12px">
          <el-icon size="14"><Upload /></el-icon> 上传
        </el-button>
        <el-button size="small" type="danger" :disabled="selectedIds.length === 0" @click="handleBatchDelete" style="height:28px;font-size:12px">
          <el-icon size="14"><Delete /></el-icon> 删 ({{ selectedIds.length }})
        </el-button>
      </div>
    </div>

    <div class="media-container">
      <!-- 左侧目录树 -->
      <div class="media-sidebar">
        <div class="sidebar-header">
          <span>素材目录</span>
          <el-button size="small" text style="padding:2px 4px" @click="showCategoryDialog(null)">
            <el-icon size="14"><FolderAdd /></el-icon>
          </el-button>
        </div>
        <el-input v-model="categoryFilter" placeholder="搜索目录..." size="small" clearable style="margin-bottom:4px" />
        <!-- 全部素材（放在树上方） -->
        <div class="all-media-item"
          :class="{ active: currentCategoryId === null }"
          @click="handleCategoryClick(null)">
          <el-icon size="12" style="margin-right:4px"><PictureFilled /></el-icon>
          <span>全部素材</span>
        </div>
        <el-tree
          ref="categoryTreeRef"
          :data="filteredCategoryTree"
          :props="{ label: 'name', children: 'children' }"
          node-key="id"
          :highlight-current="true"
          :expand-on-click-node="false"
          default-expand-all
          @node-click="handleCategoryClick"
        >
          <template #default="{ node, data }">
            <span class="category-node">
              <el-icon size="12" style="color:#e6a23c;margin-right:4px"><FolderOpened /></el-icon>
              <span class="node-label">{{ node.label }}</span>
              <span class="category-actions">
                <el-button size="small" text style="padding:0 2px" @click.stop="showAddChildDialog(data)"><el-icon size="12"><FolderAdd /></el-icon></el-button>
                <el-button size="small" text style="padding:0 2px" @click.stop="showCategoryDialog(data)"><el-icon size="12"><Edit /></el-icon></el-button>
                <el-button size="small" text style="padding:0 2px" type="danger" @click.stop="handleDeleteCategory(data)"><el-icon size="12"><Delete /></el-icon></el-button>
              </span>
            </span>
          </template>
        </el-tree>
      </div>

      <!-- 右侧主区域 -->
      <div class="media-main">
        <!-- 工具栏（单行） -->
        <div class="toolbar">
          <el-input v-model="searchKeyword" placeholder="搜索名称..." clearable size="small" style="width:180px" @input="handleSearch" />
          <el-select v-model="filterType" placeholder="类型" size="small" clearable style="width:90px" @change="handleSearch">
            <el-option label="全部" value="" />
            <el-option label="图片" value="image" />
            <el-option label="视频" value="video" />
          </el-select>
          <span class="toolbar-count">共 {{ pagination.total }} 条</span>
        </div>

        <!-- 素材网格（行分割） -->
        <div v-loading="loading" class="media-grid">
          <div v-for="(row, ri) in rows" :key="ri" class="media-row">
            <div
              v-for="item in row"
              :key="item.id"
              class="media-item"
              :class="{ selected: selectedIds.includes(item.id) }"
              @click="toggleSelect(item)"
          >
            <div v-if="item.type === 'image'" class="media-preview">
              <img :src="item.url" class="media-preview-img" />
            </div>
            <div v-else class="media-preview video-preview">
              <video :src="item.url" muted preload="metadata" />
              <div class="video-badge"><el-icon size="16"><VideoCamera /></el-icon></div>
            </div>

            <div class="media-bar" @click.stop>
              <span class="bar-name" :title="item.name">{{ item.name }}</span>
              <button class="bar-btn" title="预览" @click="handlePreview(item)">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              </button>
              <button class="bar-btn" title="复制链接" @click="handleCopyUrl(item.url)">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
              </button>
            </div>

            <div v-if="selectedIds.includes(item.id)" class="selected-mark">
              <el-icon size="12"><Check /></el-icon>
            </div>
          </div>
          </div>

          <div v-if="!loading && mediaList.length === 0" class="empty-state">
            <el-empty :description="searchKeyword ? '无匹配结果' : '暂无素材'" :image-size="60" />
          </div>
        </div>

        <!-- 分页 -->
        <div class="pagination-bar" v-if="pagination.total > 0">
          <el-pagination
            v-model:current-page="pagination.current_page"
            v-model:page-size="pagination.per_page"
            :page-sizes="[28, 56, 84]"
            :total="pagination.total"
            layout="prev, pager, next, sizes"
            small
            @change="fetchMedia"
          />
        </div>
      </div>
    </div>

    <!-- 上传弹窗 -->
    <el-dialog v-model="uploadDialogVisible" title="上传素材" width="400px">
      <div style="display:flex;align-items:center;gap:6px;margin-bottom:12px">
        <span style="font-size:12px;color:#606266;white-space:nowrap">上传到：</span>
        <el-tree-select v-model="uploadCategoryId" :data="categoryTree" :props="{ label:'name', children:'children', value:'id' }" placeholder="未分类" clearable check-strictly size="small" style="width:200px" />
      </div>
      <el-upload
        ref="uploadRef"
        multiple
        :action="uploadAction"
        :headers="uploadHeaders"
        :data="{ category_id: uploadCategoryId }"
        :show-file-list="true"
        :file-list="uploadFileList"
        :on-success="handleUploadSuccess"
        :on-error="handleUploadError"
        :before-upload="beforeUpload"
        :on-remove="handleUploadRemove"
        accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/mpeg,video/quicktime,video/webm"
      >
        <el-button size="small" type="primary"><el-icon size="14"><Upload /></el-icon> 选择文件</el-button>
        <span style="margin-left:8px;font-size:11px;color:#909399">JPG/PNG/WebP ≤5MB，MP4/WebM ≤100MB</span>
      </el-upload>
      <template #footer><el-button size="small" @click="uploadDialogVisible=false">关闭</el-button></template>
    </el-dialog>

    <!-- 目录编辑弹窗 -->
    <el-dialog v-model="categoryDialogVisible" :title="editingCategory ? '编辑目录' : '新建目录'" width="380px">
      <el-form ref="categoryFormRef" :model="categoryForm" :rules="categoryRules" label-width="60px">
        <el-form-item label="名称" prop="name"><el-input v-model="categoryForm.name" placeholder="目录名称" size="small" /></el-form-item>
        <el-form-item label="上级">
          <el-tree-select v-model="categoryForm.parent_id" :data="categoryTree" :props="{ label:'name', children:'children', value:'id' }" placeholder="顶级目录" clearable check-strictly :disabled="editingCategory!==null" size="small" style="width:100%" />
        </el-form-item>
        <el-form-item label="排序"><el-input-number v-model="categoryForm.sort_order" :min="0" size="small" /></el-form-item>
      </el-form>
      <template #footer>
        <el-button size="small" @click="categoryDialogVisible=false">取消</el-button>
        <el-button size="small" type="primary" @click="handleSaveCategory" :loading="categorySubmitting">保存</el-button>
      </template>
    </el-dialog>

    <!-- 编辑素材 -->
    <el-dialog v-model="editDialogVisible" title="编辑素材" width="380px">
      <el-form ref="editFormRef" :model="editForm" :rules="editRules" label-width="60px">
        <el-form-item label="名称" prop="name"><el-input v-model="editForm.name" size="small" /></el-form-item>
        <el-form-item label="目录">
          <el-tree-select v-model="editForm.category_id" :data="categoryTree" :props="{ label:'name', children:'children', value:'id' }" placeholder="无" clearable check-strictly size="small" style="width:100%" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button size="small" @click="editDialogVisible=false">取消</el-button>
        <el-button size="small" type="primary" @click="handleSaveEdit" :loading="editSubmitting">保存</el-button>
      </template>
    </el-dialog>

    <!-- 大图预览 -->
    <el-dialog v-model="previewDialogVisible" :title="previewName" width="800px" top="5vh">
      <div class="preview-image-wrap"><el-image :src="previewUrl" fit="contain" style="width:100%;max-height:75vh" /></div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import * as mediaApi from '../../api/media'

const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || '/api'
const uploadAction = apiBaseUrl + '/manage/media/upload'
const uploadHeaders = { Authorization: 'Bearer ' + (localStorage.getItem('token') || '') }

const categoryTree = ref([])
const categoryTreeRef = ref(null)
const categoryFilter = ref('')
const currentCategoryId = ref(null)
const mediaList = ref([])
const loading = ref(false)
const searchKeyword = ref('')
const filterType = ref('')
const selectedIds = ref([])
const pagination = reactive({ current_page: 1, per_page: 28, total: 0 })

const uploadDialogVisible = ref(false)
const uploadRef = ref(null)
const uploadFileList = ref([])
const uploadCategoryId = ref(null)

const previewDialogVisible = ref(false)
const previewUrl = ref('')
const previewName = ref('')

const editDialogVisible = ref(false)
const editFormRef = ref(null)
const editSubmitting = ref(false)
const editForm = reactive({ id: null, name: '', category_id: null })
const editRules = { name: [{ required: true, message: '请输入名称', trigger: 'blur' }] }

const categoryDialogVisible = ref(false)
const editingCategory = ref(null)
const categoryFormRef = ref(null)
const categorySubmitting = ref(false)
const categoryForm = reactive({ name: '', parent_id: null, sort_order: 0 })
const categoryRules = { name: [{ required: true, message: '请输入目录名称', trigger: 'blur' }] }

const filteredCategoryTree = computed(() => {
  if (!categoryFilter.value) return categoryTree.value
  return filterTree(categoryTree.value, categoryFilter.value)
})
function filterTree(nodes, kw) {
  const l = kw.toLowerCase()
  return nodes.map(n => {
    const m = n.name.toLowerCase().includes(l)
    const ch = n.children ? filterTree(n.children, kw) : []
    return (m || ch.length) ? { ...n, children: ch } : null
  }).filter(Boolean)
}

const fetchMedia = async () => {
  loading.value = true
  try {
    const p = { page: pagination.current_page, per_page: pagination.per_page, type: filterType.value || undefined, search: searchKeyword.value || undefined }
    if (currentCategoryId.value) p.category_id = currentCategoryId.value
    const r = await mediaApi.getMediaList(p)
    mediaList.value = r.data || []
    pagination.current_page = r.pagination?.current_page || 1
    pagination.total = r.pagination?.total || 0
    const ids = new Set(mediaList.value.map(i => i.id))
    selectedIds.value = selectedIds.value.filter(id => ids.has(id))
  } catch {} finally { loading.value = false }
}

const fetchCategoryTree = async () => {
  try { const r = await mediaApi.getMediaCategoryTree(); categoryTree.value = r.data || [] } catch {}
}

const handleCategoryClick = (data, node) => {
  currentCategoryId.value = node?.key || data?.id || null
  pagination.current_page = 1
  fetchMedia()
}
const handleSearch = () => { pagination.current_page = 1; fetchMedia() }
const toggleSelect = (item) => {
  const i = selectedIds.value.indexOf(item.id)
  i > -1 ? selectedIds.value.splice(i, 1) : selectedIds.value.push(item.id)
}

const colsPerRow = 7
const rows = computed(() => {
  const result = []
  for (let i = 0; i < mediaList.value.length; i += colsPerRow) {
    result.push(mediaList.value.slice(i, i + colsPerRow))
  }
  return result
})

const handleCopyUrl = (u) => { navigator.clipboard?.writeText(u).then(() => ElMessage.success('已复制')) }
const handlePreview = (item) => { previewUrl.value = item.url; previewName.value = item.name; previewDialogVisible.value = true }

// upload
const showUploadDialog = () => { uploadFileList.value = []; uploadCategoryId.value = currentCategoryId.value; uploadDialogVisible.value = true }
const beforeUpload = (f) => {
  if (!uploadCategoryId.value) { ElMessage.warning('请选择目录'); return false }
  if (!f.type.startsWith('image/') && !f.type.startsWith('video/')) { ElMessage.error('仅支持图片/视频'); return false }
  if (f.type.startsWith('image/') && f.size > 5*1024*1024) { ElMessage.error('图片≤5MB'); return false }
  if (f.type.startsWith('video/') && f.size > 100*1024*1024) { ElMessage.error('视频≤100MB'); return false }
  return true
}
const handleUploadSuccess = (res, file) => {
  if (res.code === 200 || res.errno === 0) { ElMessage.success(file.name + ' 上传成功'); uploadFileList.value = uploadFileList.value.filter(f => f.uid !== file.uid); fetchMedia() }
  else { ElMessage.error(res.message || '失败') }
}
const handleUploadError = (_, file) => { ElMessage.error(file.name + ' 失败') }
const handleUploadRemove = (file) => { uploadFileList.value = uploadFileList.value.filter(f => f.uid !== file.uid) }

// batch delete
const handleBatchDelete = () => {
  ElMessageBox.confirm('确定删除选中的素材？', '提示', { type: 'warning' }).then(async () => {
    try { await mediaApi.batchDeleteMedia(selectedIds.value); ElMessage.success('已删除'); selectedIds.value = []; fetchMedia() }
    catch { ElMessage.error('失败') }
  }).catch(() => {})
}

// category
const showAddChildDialog = (p) => { editingCategory.value = null; categoryForm.name = ''; categoryForm.parent_id = p.id; categoryForm.sort_order = 0; categoryDialogVisible.value = true }
const showCategoryDialog = (d) => {
  editingCategory.value = d
  categoryForm.name = d?.name || ''; categoryForm.parent_id = d?.parent_id || null; categoryForm.sort_order = d?.sort_order || 0
  categoryDialogVisible.value = true
}
const handleSaveCategory = async () => {
  const ok = await categoryFormRef.value?.validate().catch(() => false); if (!ok) return
  categorySubmitting.value = true
  try {
    if (editingCategory.value) await mediaApi.updateMediaCategory(editingCategory.value.id, { ...categoryForm })
    else await mediaApi.createMediaCategory({ ...categoryForm })
    ElMessage.success('保存成功'); categoryDialogVisible.value = false; fetchCategoryTree()
  } catch { ElMessage.error('失败') } finally { categorySubmitting.value = false }
}
const handleDeleteCategory = (d) => {
  ElMessageBox.confirm('确定删除目录「' + d.name + '」吗？', '提示', { type: 'warning' }).then(async () => {
    try { await mediaApi.deleteMediaCategory(d.id); ElMessage.success('已删除'); fetchCategoryTree() } catch {}
  }).catch(() => {})
}

// edit media
const showEditDialog = (item) => { editForm.id = item.id; editForm.name = item.name; editForm.category_id = item.category_id; editDialogVisible.value = true }
const handleSaveEdit = async () => {
  const ok = await editFormRef.value?.validate().catch(() => false); if (!ok) return
  editSubmitting.value = true
  try { await mediaApi.updateMedia(editForm.id, { name: editForm.name, category_id: editForm.category_id }); ElMessage.success('已保存'); editDialogVisible.value = false; fetchMedia() }
  catch { ElMessage.error('失败') } finally { editSubmitting.value = false }
}

const handleDelete = (item) => {
  ElMessageBox.confirm('确定删除「' + item.name + '」？', '提示', { type: 'warning' }).then(async () => {
    try { await mediaApi.deleteMedia(item.id); ElMessage.success('已删除'); fetchMedia() } catch { ElMessage.error('失败') }
  }).catch(() => {})
}

onMounted(() => { fetchCategoryTree(); fetchMedia() })
</script>

<style scoped>
.media-manager { display:flex; flex-direction:column; height:calc(100vh - 120px) }

.page-header h2 { font-size:16px; margin:0 }
.header-actions { display:flex; gap:6px }

/* ====== 左右布局 ====== */
.media-container { display:flex; gap:0; flex:1; overflow:hidden }

/* ====== 左侧目录 200px ====== */
.media-sidebar {
  width:200px; flex-shrink:0;
  border-right:1px solid #e4e7ed; padding-right:10px;
  display:flex; flex-direction:column; overflow:hidden;
}
.sidebar-header { display:flex; justify-content:space-between; align-items:center; font-weight:600; font-size:13px; padding:4px 0 6px }
.all-media-item {
  display:flex; align-items:center; gap:2px;
  padding:3px 8px; margin:2px 0; border-radius:4px;
  cursor:pointer; font-size:12px; color:#606266;
  height:28px; box-sizing:border-box;
}
.all-media-item:hover { background:#f5f5f5; color:#333 }
.all-media-item.active { color:#9E8569; font-weight:600; background:#fdf8f0 }
.sidebar-footer { margin-top:auto; padding:6px 0 4px; border-top:1px solid #e4e7ed }
.category-node { display:flex; align-items:center; flex:1; min-width:0; height:28px }
.category-node .node-label { flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-size:12px }
.category-actions { opacity:0; display:flex; gap:0; flex-shrink:0 }
.category-node:hover .category-actions { opacity:1 }

/* ====== 右侧主区域 ====== */
.media-main { flex:1; display:flex; flex-direction:column; overflow:hidden; min-width:0; margin-left:12px }

/* ====== 工具栏 ====== */
.toolbar { display:flex; align-items:center; gap:8px; padding:0 0 8px; flex-shrink:0 }
.toolbar-count { font-size:12px; color:#909399 }

/* ====== 行分割素材网格 ====== */
.media-grid { flex:1; overflow-y:auto; padding:2px }
.media-row { display:grid; grid-template-columns:repeat(7, 1fr); gap:12px; margin-bottom:28px }
.media-row:last-child { margin-bottom:0 }

.media-item {
  position:relative;
  border:2px solid #e4e7ed; border-radius:4px;
  cursor:pointer; background:#f5f5f5;
  aspect-ratio:1/1; overflow:hidden;
  transition:border-color .15s, background .15s;
}
.media-item:hover { border-color:#9E8569 }
.media-item.selected { border-color:#9E8569; background:#fdf8f0 }

.media-preview { width:100%; height:100%; overflow:hidden }
.media-preview-img, .media-preview video { width:100%; height:100%; object-fit:cover; display:block }
.video-preview { position:relative }
.video-badge { position:absolute; top:50%; left:50%; transform:translate(-50%,-50%); color:rgba(255,255,255,.9); pointer-events:none }

/* ====== 底部信息条（absolute 浮在卡片底部） ====== */
.media-bar {
  position:absolute; bottom:0; left:0; right:0;
  display:flex; align-items:center; gap:2px;
  padding:3px 6px;
  background:rgba(0,0,0,.5); backdrop-filter:blur(3px);
  height:26px; box-sizing:border-box;
}
.bar-name { flex:1; font-size:11px; color:#fff; overflow:hidden; text-overflow:ellipsis; white-space:nowrap }
.bar-btn { background:none; border:none; color:#fff; cursor:pointer; padding:2px; border-radius:2px; display:flex; opacity:.7; flex-shrink:0 }
.bar-btn:hover { opacity:1; background:rgba(255,255,255,.15) }

/* ====== 选中标记 ====== */
.selected-mark {
  position:absolute; top:3px; right:3px;
  width:18px; height:18px; border-radius:50%;
  background:#9E8569; color:#fff;
  display:flex; align-items:center; justify-content:center;
}

.empty-state { grid-column:1/-1; display:flex; justify-content:center; padding:40px 0 }
.pagination-bar { flex-shrink:0; display:flex; justify-content:flex-end; padding-top:8px }
.preview-image-wrap { display:flex; align-items:center; justify-content:center; min-height:150px }
</style>
